<script lang="ts">
    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';

    // Shared Components
    import Chevron from '@amp/web-app-components/assets/icons/chevron.svg';

    // Shared Utilities
    import { sanitizeHtml } from '@amp/web-app-components/src/utils/sanitize-html';
    import { sidebarIsHidden } from '@amp/web-app-components/src/stores/sidebar-hidden';

    // Types
    import type { SearchSuggestionLockup } from '@jet-app/music-classical/models/pages/search';
    import { ContentKind } from '@jet-app/music-classical/models/content-descriptor/ContentKind';

    export let item: SearchSuggestionLockup;

    $: isArtist = item.type === ContentKind.Artist;
    $: isWork = item.type === ContentKind.Work;
    $: artworkProfile = `top-search-result`;
</script>

<div
    class="top-search-list-lockup"
    data-testid="top-search-list-result"
    class:top-search-list-lockup--artist={isArtist}
>
    <div class="top-search-list-lockup__content">
        <div
            class="top-search-list-lockup__artwork"
            class:top-search-list-lockup__artwork--circle={isArtist || isWork}
        >
            <Artwork
                artwork={item.artwork}
                profile={artworkProfile}
                fallbackKind={item.type}
                forceFullWidth={false}
            />
        </div>
        <ul class="top-search-list-lockup__description">
            {#if item.headline}
                <li
                    class="top-search-list-lockup__headline"
                    data-testid="top-search-list-result-headline"
                    dir="auto"
                >
                    <span dir="auto">
                        {@html sanitizeHtml(item.headline)}
                    </span>
                </li>
            {/if}
            <li
                class="top-search-list-lockup__explicit-wrapper"
                data-testid="top-search-list-result-title"
            >
                <span class="top-search-list-lockup__primary" dir="auto">
                    {item.title}
                </span>
                <!-- <FavoriteBadge {inFavorites} /> -->
            </li>
            <!-- dir="auto" is needed on the <li> and the <span> for proper directional flow  -->
            <li
                class="top-search-list-lockup__secondary"
                data-testid="top-search-list-result-subtitle"
                dir="auto"
            >
                <span dir="auto">
                    {item.subtitle}
                </span>
            </li>
        </ul>
    </div>
    <div class="top-search-list-lockup__icons">
        {#if $sidebarIsHidden}
            <Chevron
                class="top-search-list-lockup__icon"
                aria-hidden="true"
                data-testid="top-search-list-result-chevron"
            />
        {/if}
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/aspect-ratio' as *;

    $top-search-hint-list-lockup-image-size: 36px;
    $top-search-result-list-lockup-image-size: 56px;

    // Used to extend gutter to 12pt when sidebar is visible.
    $top-search-list-gutter: 6px;

    .top-search-list-lockup {
        padding-top: 10px;
        padding-bottom: 11px;
        width: 100%;
        height: auto;
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-areas: 'content icons';
        align-items: center;
        position: relative;
        cursor: pointer;
        z-index: var(--z-default);

        @media (--sidebar-visible) {
            padding-top: 8px;
            padding-bottom: 10px;
            min-height: 48px;
            padding-inline-start: 7px;
            padding-inline-end: 7px;

            &:has(.top-search-list-lockup__headline) {
                padding-top: 10px;
            }
        }

        a:hover {
            text-decoration: none;
        }
    }

    .top-search-list-lockup__content {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-gap: 10px;
        align-items: center;
    }

    .top-search-list-lockup__description {
        flex: 1;
        flex-shrink: 1;
        display: block;
        overflow: hidden;
    }

    .top-search-list-lockup__primary {
        color: var(--systemPrimary);
        font: var(--title-3);

        @include line-clamp(2);

        @media (--sidebar-visible) {
            font: var(--body);
        }
    }

    .top-search-list-lockup__secondary {
        color: var(--systemSecondary);
        font: var(--body);

        @include line-clamp;

        @media (--sidebar-visible) {
            margin-top: 1px;
            font: var(--callout);
        }
    }

    .top-search-list-lockup__headline {
        color: var(--systemSecondary);
        font: var(--subhead-emphasized);

        @media (--sidebar-visible) {
            margin-top: 1px;
            font: var(--callout);
        }
    }

    .top-search-list-lockup__artwork {
        position: relative;
        border-radius: $global-border-radius-small;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        z-index: var(--z-default);

        :global(.artwork-component) {
            @include aspect-ratio(
                $height: $top-search-result-list-lockup-image-size
            );

            @media (--sidebar-visible) {
                @include aspect-ratio(
                    $height: $top-search-hint-list-lockup-image-size
                );
            }
        }
    }

    .top-search-list-lockup__artwork--circle {
        border-radius: 50%;
        overflow: hidden;
    }

    .top-search-list-lockup__icons {
        height: 28px;
        display: grid;
        grid-gap: 8px;
        grid-template-areas: 'col1';
        align-items: center;
        justify-content: center;
        margin-inline-start: 10px;

        :global(.top-search-list-lockup__icon) {
            height: 15px;
            width: 15px;
            fill: var(--systemTertiary);

            @include rtl {
                // Flip Chevron for RTL
                transform: scaleX(-1);
            }
        }
    }
</style>
